import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FlaskConical,
  ChartNoAxesCombined,
  LayoutDashboard,
  LogOut,
  ScrollText,
  Shuffle,
  SlidersHorizontal
} from 'lucide-react';
import { Link, useLocation, useMatch, useNavigate } from 'react-router-dom';
import { api } from '../api';
import type { AccountView } from '../types';
import { TradeControl } from './TradeControl';

const navItems = [
  { to: '/', label: '账户总览', mobileLabel: '账户总览', icon: LayoutDashboard },
  { to: '/orders', label: '历史记录', mobileLabel: '历史记录', icon: ScrollText },
  { to: '/performance', label: '盈亏曲线', mobileLabel: '盈亏曲线', icon: ChartNoAxesCombined },
  { to: '/monte-carlo', label: '蒙特卡洛模拟', mobileLabel: '模拟', icon: Shuffle },
  { to: '/strategy', label: '币种策略参数', mobileLabel: '参数', icon: SlidersHorizontal },
  { to: '/test', label: 'API 测试工具', mobileLabel: 'API 测试', icon: FlaskConical }
];

export function Shell({ username, children }: { username: string; children: ReactNode }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const accountRoute = useMatch('/accounts/:accountId');
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: () => api<AccountView[]>('/api/accounts'), staleTime: Infinity, refetchOnWindowFocus: false });
  const scoped = Boolean(accountRoute) || ['/orders', '/performance', '/monte-carlo'].includes(pathname);
  const selectedId = accountRoute?.params.accountId ?? new URLSearchParams(search).get('account');
  const selectedSymbol = new URLSearchParams(search).get('symbol');
  const selected = accounts.data?.find((account) => account.id === selectedId) ?? (accountRoute ? undefined : accounts.data?.[0]);
  const readOnlyContext = (scoped && (!accounts.data || selected?.purpose === 'read_only')) || Boolean(accounts.data?.length && accounts.data.every((account) => account.purpose === 'read_only'));
  const logout = useMutation({
    mutationFn: () => api('/api/auth/logout', { method: 'POST' }),
    onSuccess: async () => {
      queryClient.clear();
      navigate('/');
      window.location.reload();
    }
  });

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-symbol"><img src="/icons/app-icon-192.png" alt="" /></div>
          <div><strong>EMA · ATR</strong><span>币种独立参数 · 4H</span></div>
        </div>

        <nav className="main-nav" aria-label="主导航">
          {navItems.map(({ to, label, mobileLabel, icon: Icon }) => {
            const active = to === '/' ? pathname === '/' || pathname.startsWith('/accounts/') : pathname === to;
            return (
              <Link key={to} to={scoped && selected && ['/orders', '/performance', '/monte-carlo'].includes(to) ? `${to}?${new URLSearchParams({ account: selected.id, ...(selectedSymbol ? { symbol: selectedSymbol } : {}) })}` : to} aria-label={label} aria-current={active ? 'page' : undefined} className={active ? 'nav-item active' : 'nav-item'}>
                <Icon size={21} />
                <span className="nav-label">{label}</span><span className="nav-label-compact">{mobileLabel}</span>
              </Link>
            );
          })}
        </nav>

        <div className="sidebar-foot">
          <div className="operator-avatar">{username.slice(0, 1).toUpperCase()}</div>
          <div className="operator-copy"><strong>{username}</strong></div>
          <button className="icon-button" onClick={() => logout.mutate()} aria-label="退出登录"><LogOut size={17} /></button>
        </div>
      </aside>
      <div className="workspace">
        <TradeControl readOnlyContext={readOnlyContext} />
        <main className="main-content">{children}</main>
      </div>
    </div>
  );
}
